import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

const emptyForm = {
  name: '', description: '', category: '', subcategory: '',
  material: '', finish: '', grade: '', series: '',
  isAvailable: true, isFeatured: false,
  tags: '', sizes: [], specifications: [],
};

export default function AdminProducts() {
  const [products,      setProducts]      = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filteredSubs,  setFilteredSubs]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [modal,         setModal]         = useState(false);
  const [editing,       setEditing]       = useState(null);
  const [form,          setForm]          = useState(emptyForm);
  const [images,        setImages]        = useState([]);        // new File[]
  const [existingImages,setExistingImages]= useState([]);        // existing URL[]
  const [saving,        setSaving]        = useState(false);
  const [search,        setSearch]        = useState('');
  const [catFilter,     setCatFilter]     = useState('');
  const [page,          setPage]          = useState(1);
  const [pagination,    setPagination]    = useState({});
  const fileRef = useRef();

  // size / spec helpers
  const [newSize, setNewSize] = useState({ label: '', sku: '' });
  const [newSpec, setNewSpec] = useState({ label: '', value: '' });

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 15 });
    if (search)    params.set('search',   search);
    if (catFilter) params.set('category', catFilter);
    api.get(`/products?${params}`)
      .then(r => { setProducts(r.data.data || []); setPagination(r.data.pagination || {}); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page, catFilter]); // eslint-disable-line

  useEffect(() => {
    api.get('/categories/admin').then(r => setCategories(r.data.data || []));
    api.get('/subcategories/admin').then(r => setSubcategories(r.data.data || []));
  }, []);

  useEffect(() => {
    setFilteredSubs(form.category ? subcategories.filter(s => s.category?._id === form.category || s.category === form.category) : []);
  }, [form.category, subcategories]);

  const openAdd = () => {
    setEditing(null); setForm(emptyForm);
    setImages([]); setExistingImages([]); setModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description || '',
      category: p.category?._id || '', subcategory: p.subcategory?._id || '',
      material: p.material || '', finish: p.finish || '',
      grade: p.grade || '', series: p.series || '',
      isAvailable: p.isAvailable, isFeatured: p.isFeatured,
      tags: Array.isArray(p.tags) ? p.tags.join(', ') : '',
      sizes: p.sizes || [], specifications: p.specifications || [],
    });
    setImages([]); setExistingImages(p.images || []); setModal(true);
  };

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const addSize = () => {
    if (!newSize.label) return;
    setForm(f => ({ ...f, sizes: [...f.sizes, { ...newSize }] }));
    setNewSize({ label: '', sku: '' });
  };
  const removeSize = (i) => setForm(f => ({ ...f, sizes: f.sizes.filter((_, idx) => idx !== i) }));

  const addSpec = () => {
    if (!newSpec.label || !newSpec.value) return;
    setForm(f => ({ ...f, specifications: [...f.specifications, { ...newSpec }] }));
    setNewSpec({ label: '', value: '' });
  };
  const removeSpec = (i) => setForm(f => ({ ...f, specifications: f.specifications.filter((_, idx) => idx !== i) }));

  const removeExistingImage = (url) => setExistingImages(ei => ei.filter(u => u !== url));

  const save = async () => {
    if (!form.name || !form.category) { toast.error('Name and category are required.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'sizes' || k === 'specifications') fd.append(k, JSON.stringify(v));
        else fd.append(k, v);
      });
      fd.append('existingImages', JSON.stringify(existingImages));
      images.forEach(f => fd.append('images', f));

      if (editing) {
        const r = await api.put(`/products/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setProducts(ps => ps.map(p => p._id === editing._id ? r.data.data : p));
        toast.success('Product updated!');
      } else {
        const r = await api.post('/products', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setProducts(ps => [r.data.data, ...ps]);
        toast.success('Product created!');
      }
      setModal(false);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await api.delete(`/products/${id}`); setProducts(ps => ps.filter(p => p._id !== id)); toast.success('Deleted.'); }
    catch { toast.error('Failed to delete.'); }
  };
  const sortedProducts = [...products].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  const handleSearch = e => { e.preventDefault(); setPage(1); load(); };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Products</h1>
          <p className="font-body text-sm text-secondary mt-1">{pagination.total || 0} total products</p>
        </div>
        <button onClick={openAdd} className="btn-primary">
          <span className="material-symbols-outlined">add</span> Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..."
            className="input-field flex-1 py-2 text-sm" />
          <button type="submit" className="btn-primary py-2 px-4">
            <span className="material-symbols-outlined text-[18px]">search</span>
          </button>
        </form>
        <select value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}
          className="input-field w-auto py-2 text-sm">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-14 skeleton" />)}</div>
      ) : (
        <div className="bg-white border border-outline-variant/20 overflow-x-auto">
          <table className="cf-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Sizes</th>
                <th>Status</th>
                <th>Featured</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedProducts.map(p => (
                <tr key={p._id}>
                  <td>
                    {p.images?.[0]
                      ? <img src={p.images[0]} alt={p.name} className="w-12 h-12 object-cover" />
                      : <div className="w-12 h-12 bg-surface-container flex items-center justify-center">
                          <span className="material-symbols-outlined text-[20px] text-outline-variant">bolt</span>
                        </div>
                    }
                  </td>
                  <td>
                    <p className="font-bold text-sm">{p.name}</p>
                    {p.grade && <p className="text-[10px] text-secondary">{p.grade} · {p.material}</p>}
                  </td>
                  <td className="text-secondary text-sm">{p.category?.name}</td>
                  <td className="text-secondary text-sm">{p.sizes?.length || 0}</td>
                  <td><span className={`badge ${p.isAvailable ? 'badge-green' : 'badge-red'}`}>{p.isAvailable ? 'Active' : 'Off'}</span></td>
                  <td>
                    {p.isFeatured
                      ? <span className="material-symbols-outlined text-yellow-500 text-[18px]">star</span>
                      : <span className="text-outline-variant text-xs">—</span>}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(p)} className="p-1.5 text-secondary hover:text-primary-container transition-colors">
                        <span className="material-symbols-outlined text-[18px]">edit</span>
                      </button>
                      <button onClick={() => del(p._id)} className="p-1.5 text-secondary hover:text-error transition-colors">
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-secondary">No products found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="px-3 py-2 border border-outline-variant text-sm disabled:opacity-40 hover:bg-surface-container">
            <span className="material-symbols-outlined text-[18px]">chevron_left</span>
          </button>
          {[...Array(pagination.pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={`w-9 h-9 text-sm font-bold ${page === i + 1 ? 'bg-primary-container text-white' : 'border border-outline-variant hover:bg-surface-container'}`}>
              {i + 1}
            </button>
          ))}
          <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}
            className="px-3 py-2 border border-outline-variant text-sm disabled:opacity-40 hover:bg-surface-container">
            <span className="material-symbols-outlined text-[18px]">chevron_right</span>
          </button>
        </div>
      )}

      {/* Product Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-box w-full max-w-3xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-container sticky top-0 bg-white z-10">
              <h2 className="font-headline font-black text-xl uppercase">{editing ? 'Edit Product' : 'Add Product'}</h2>
              <button onClick={() => setModal(false)}><span className="material-symbols-outlined">close</span></button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="input-label">Product Name *</label>
                  <input name="name" value={form.name} onChange={h} className="input-field" placeholder="e.g. Hex Bolt M10" />
                </div>
                <div>
                  <label className="input-label">Category *</label>
                  <select name="category" value={form.category} onChange={h} className="input-field">
                    <option value="">Select category...</option>
                    {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="input-label">Subcategory</label>
                  <select name="subcategory" value={form.subcategory} onChange={h} className="input-field" disabled={!form.category}>
                    <option value="">None</option>
                    {filteredSubs.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="input-label">Material</label>
                  <input name="material" value={form.material} onChange={h} className="input-field" placeholder="e.g. Stainless Steel" />
                </div>
                <div>
                  <label className="input-label">Finish</label>
                  <input name="finish" value={form.finish} onChange={h} className="input-field" placeholder="e.g. Plain, Zinc Plated" />
                </div>
                <div>
                  <label className="input-label">Grade</label>
                  <input name="grade" value={form.grade} onChange={h} className="input-field" placeholder="e.g. 8.8, A2-70" />
                </div>
                <div>
                  <label className="input-label">Series / Standard</label>
                  <input name="series" value={form.series} onChange={h} className="input-field" placeholder="e.g. DIN 931, IS 1363" />
                </div>
                <div className="sm:col-span-2">
                  <label className="input-label">Description</label>
                  <textarea name="description" value={form.description} onChange={h} rows={3}
                    className="input-field resize-none" placeholder="Product description..." />
                </div>
                <div>
                  <label className="input-label">Tags (comma separated)</label>
                  <input name="tags" value={form.tags} onChange={h} className="input-field" placeholder="hex, bolt, fastener" />
                </div>
                <div className="flex items-end gap-6 pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="isAvailable" checked={!!form.isAvailable} onChange={h} className="w-4 h-4 accent-primary-container" />
                    <span className="font-label text-sm font-bold uppercase tracking-wider">Available</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="isFeatured" checked={!!form.isFeatured} onChange={h} className="w-4 h-4 accent-yellow-500" />
                    <span className="font-label text-sm font-bold uppercase tracking-wider">Featured</span>
                  </label>
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="input-label">Product Images</label>
                {existingImages.length > 0 && (
                  <div className="flex flex-wrap gap-3 mb-3">
                    {existingImages.map(url => (
                      <div key={url} className="relative">
                        <img src={url} alt="" className="w-20 h-20 object-cover border border-outline-variant/20" />
                        <button type="button" onClick={() => removeExistingImage(url)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-error text-white rounded-full flex items-center justify-center text-[10px]">✕</button>
                      </div>
                    ))}
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" multiple
                  onChange={e => setImages(Array.from(e.target.files))}
                  className="input-field" />
                {images.length > 0 && (
                  <p className="font-label text-xs text-secondary mt-1">{images.length} new image{images.length !== 1 ? 's' : ''} selected</p>
                )}
              </div>

              {/* Sizes */}
              <div>
                <label className="input-label">Sizes</label>
                <div className="space-y-2 mb-3">
                  {form.sizes.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 bg-surface-container-low px-3 py-2">
                      <span className="font-label text-sm font-bold flex-1">{s.label}</span>
                      {s.sku && <span className="font-label text-xs text-secondary font-mono">{s.sku}</span>}
                      <button type="button" onClick={() => removeSize(i)} className="text-error hover:opacity-70">
                        <span className="material-symbols-outlined text-[16px]">close</span>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={newSize.label} onChange={e => setNewSize(n => ({ ...n, label: e.target.value }))}
                    className="input-field flex-1 py-2 text-sm" placeholder="Size label (e.g. M10 x 50)" />
                  <input value={newSize.sku} onChange={e => setNewSize(n => ({ ...n, sku: e.target.value }))}
                    className="input-field w-32 py-2 text-sm" placeholder="SKU" />
                  <button type="button" onClick={addSize} className="btn-primary py-2 px-4 text-sm">
                    <span className="material-symbols-outlined text-[18px]">add</span>
                  </button>
                </div>
              </div>

              {/* Specifications */}
              <div>
                <label className="input-label">Technical Specifications</label>
                <div className="space-y-2 mb-3">
                  {form.specifications.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 bg-surface-container-low px-3 py-2">
                      <span className="font-label text-xs text-secondary w-1/3">{s.label}</span>
                      <span className="font-label text-sm font-bold flex-1">{s.value}</span>
                      <button type="button" onClick={() => removeSpec(i)} className="text-error hover:opacity-70">
                        <span className="material-symbols-outlined text-[16px]">close</span>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={newSpec.label} onChange={e => setNewSpec(n => ({ ...n, label: e.target.value }))}
                    className="input-field flex-1 py-2 text-sm" placeholder="Label (e.g. Thread Pitch)" />
                  <input value={newSpec.value} onChange={e => setNewSpec(n => ({ ...n, value: e.target.value }))}
                    className="input-field flex-1 py-2 text-sm" placeholder="Value (e.g. 1.25mm)" />
                  <button type="button" onClick={addSpec} className="btn-primary py-2 px-4 text-sm">
                    <span className="material-symbols-outlined text-[18px]">add</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-surface-container flex justify-end gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setModal(false)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : <><span className="material-symbols-outlined">save</span> {editing ? 'Update' : 'Create'} Product</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
